import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';

@Injectable({
    providedIn: 'root'
})
export class BrowserApiService {
    private platformId = inject(PLATFORM_ID);


    /**
     * Safely access window object
     */
    getWindow(): Window | null {
        if (isPlatformBrowser(this.platformId)) {
            return window;
        }
        return null;
    }

    /**
     * Safely access localStorage
     */
    getLocalStorage(): Storage | null {
        if (isPlatformBrowser(this.platformId)) {
            return localStorage;
        }
        return null;
    }

    /**
     * Safely access document
     */
    getDocument(): Document | null {
        if (isPlatformBrowser(this.platformId)) {
            return document;
        }
        return null;
    }

    /**
     * Safely access navigator
     */
    getNavigator(): Navigator | null {
        if (isPlatformBrowser(this.platformId)) {
            return navigator;
        }
        return null;
    }

    /**
     * Safely execute window.scrollTo
     */
    scrollTo(options?: ScrollToOptions): void {
        if (isPlatformBrowser(this.platformId) && window) {
            window.scrollTo(options);
        }
    }

    /**
     * Safely execute window.open
     */
    open(url: string, target?: string, features?: string): Window | null {
        if (isPlatformBrowser(this.platformId) && window) {
            return window.open(url, target, features);
        }
        return null;
    }

    /**
     * Safely get window.pageYOffset
     */
    getPageYOffset(): number {
        if (isPlatformBrowser(this.platformId) && window) {
            return window.pageYOffset;
        }
        return 0;
    }

    /**
     * Safely get window.innerWidth
     */
    getInnerWidth(): number {
        if (isPlatformBrowser(this.platformId) && window) {
            return window.innerWidth;
        }
        return 1920; // Default desktop width
    }

    /**
     * Safely get window.innerHeight
     */
    getInnerHeight(): number {
        if (isPlatformBrowser(this.platformId) && window) {
            return window.innerHeight;
        }
        return 1080; // Default desktop height
    }

    /**
     * Safely access window.location
     */
    getLocation(): Location | null {
        if (isPlatformBrowser(this.platformId) && window) {
            return window.location;
        }
        return null;
    }

    /**
     * Safely create object URL
     */
    createObjectURL(blob: Blob): string {
        if (isPlatformBrowser(this.platformId) && window && window.URL) {
            return window.URL.createObjectURL(blob);
        }
        return '';
    }

    /**
     * Safely revoke object URL
     */
    revokeObjectURL(url: string): void {
        if (isPlatformBrowser(this.platformId) && window && window.URL) {
            window.URL.revokeObjectURL(url);
        }
    }

    /**
     * Safely reload the page
     */
    reload(): void {
        if (isPlatformBrowser(this.platformId) && window && window.location) {
            window.location.reload();
        }
    }

    /**
     * Check if running in browser environment
     */
    isBrowser(): boolean {
        return isPlatformBrowser(this.platformId);
    }
}
